import {
  Button,
  Checkbox,
  Divider,
  Empty,
  Input,
  List,
  Modal,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { CheckCircle, Clock, Info, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  useBulkCheckInventory,
  useCheckInventory,
  useGetInventories,
  useInventoryHistory,
} from "~/services/inventory";
import { TInventory } from "~/types";
import { IInventoryHistoryWithRelations } from "~/types/inventory-history";
import { calculateCubicFeet, capitalize } from "~/utils";
import {
  InventoryStatusCode,
  PackingTypes,
  ServiceType,
} from "~/utils/constants";

const { Title, Text } = Typography;
const { Search } = Input;

interface InventoryHistoryEntry
  extends Omit<IInventoryHistoryWithRelations, "updatedAt"> {
  updatedAt?: Date;
}

interface InventoryCheckListProps {
  serviceTicketId?: string;
  serviceType?: ServiceType;
  inventories: TInventory[];
  onInventoryChecked?: (inventoryIds: string[], checked: boolean, checkedCount?: number) => void;
  disableActions?: boolean;
  showCheckAll?: boolean;
}

export const InventoryCheckList: React.FC<InventoryCheckListProps> = ({
  serviceTicketId,
  onInventoryChecked,
  disableActions = false,
  showCheckAll = true,
}) => {
  const { data: inventories, isLoading } = useGetInventories({
    serviceTicketId,
    pageSize: 500, // Backend max limit - displays up to 500 inventory items
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [checkedInventories, setCheckedInventories] = useState<
    Record<string, boolean>
  >({});
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<TInventory | null>(
    null
  );

  // Initialize checked state based on inventory history
  useEffect(() => {
    const initialCheckedState: Record<string, boolean> = {};
    inventories?.items.forEach((item) => {
      const historyEntry = item.history?.find(
        (history) =>
          history.serviceTicketId === serviceTicketId &&
          history.inventoryId === item.id
      );
      initialCheckedState[item.id] = !!historyEntry?.checked;
    });
    setCheckedInventories(initialCheckedState);

    // Calculate initial checked count and notify parent
    const initialCheckedCount = Object.values(initialCheckedState).filter(Boolean).length;
    const checkedIds = Object.entries(initialCheckedState)
      .filter(([_, checked]) => checked)
      .map(([id]) => id);

    // Always notify parent, even if count is 0, to ensure state synchronization
    if (onInventoryChecked) {
      onInventoryChecked(checkedIds, true, initialCheckedCount);
    }
  }, [inventories, serviceTicketId, onInventoryChecked]);

  // Client-side search filtering
  const filteredInventories = inventories?.items.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.title?.toLowerCase().includes(searchLower) ||
      item.inventoryRef?.toLowerCase().includes(searchLower) ||
      item.artistName?.toLowerCase().includes(searchLower) ||
      false
    );
  });

  const allChecked =
    ((filteredInventories?.length ?? 0) > 0 &&
      (filteredInventories?.every((item) => checkedInventories[item.id]) ??
        false)) ??
    false;

  const { mutate: checkInventory } = useCheckInventory();
  const { mutate: bulkCheckInventory } = useBulkCheckInventory();
  const { data: inventoryHistory, isLoading: isLoadingHistory } =
    useInventoryHistory(selectedInventory?.id || "", !!selectedInventory);

  const formatPackingType = (packingType?: PackingTypes) => {
    if (!packingType) return "N/A";
    const packingMap: Record<PackingTypes, string> = {
      [PackingTypes.LOOSE]: "Loose",
      [PackingTypes.SLIPCASE]: "Slipcase",
      [PackingTypes.CB_FOLD]: "Cardboard Fold",
      [PackingTypes.SHADOW_BOX]: "Shadow Box",
      [PackingTypes.FLOATING_SHADOW_BOX]: "Floating Shadow Box",
      [PackingTypes.POLY_ONLY]: "Poly Only",
      [PackingTypes.TRAVEL_FRAME]: "Travel Frame",
      [PackingTypes.CRATE]: "Crate",
      [PackingTypes.SLATCRATE]: "Slat Crate",
      [PackingTypes.PALLET]: "Pallet",
      [PackingTypes.COMMERCIAL_BIN]: "Commercial Bin",
      [PackingTypes.CBIN]: "C-Bin",
      [PackingTypes.UNKNOWN_PACKAGING]: "Unknown Packaging",
      [PackingTypes.BOX]: "Box",
      [PackingTypes.CUSTOM]: "Custom",
      [PackingTypes.T_Frame]: "T-Frame",
      [PackingTypes.BLANKET_WRAPPED]: "Blanket Wrapped",
      [PackingTypes.POLY_FOAM]: "Poly Foam",
      [PackingTypes.POLY_FOAM_CB]: "Poly Foam CB",
    };
    return packingMap[packingType] || packingType;
  };

  const handleCheckInventory = (inventoryId: string, checked: boolean) => {
    const newCheckedInventories = {
      ...checkedInventories,
      [inventoryId]: checked,
    };
    setCheckedInventories(newCheckedInventories);

    // Calculate new checked count
    const newCheckedCount = Object.values(newCheckedInventories).filter(Boolean).length;

    if (!disableActions) {
      checkInventory(
        {
          inventoryId,
          checked,
          serviceTicketId,
          timestamp: new Date().toISOString(),
        },
        {
          onSuccess: () => {
            message.success(
              `Inventory ${checked ? "checked" : "unchecked"} successfully`
            );
            if (onInventoryChecked) {
              onInventoryChecked([inventoryId], checked, newCheckedCount);
            }
          },
          onError: () => {
            setCheckedInventories((prev) => ({
              ...prev,
              [inventoryId]: !checked,
            }));
            message.error("Failed to update inventory check status");
          },
        }
      );
    } else if (onInventoryChecked) {
      onInventoryChecked([inventoryId], checked, newCheckedCount);
    }
  };

  const handleCheckAll = (checked: boolean) => {
    const newCheckedInventories = { ...checkedInventories };
    const inventoryIds: string[] = [];

    filteredInventories?.forEach((item) => {
      newCheckedInventories[item.id] = checked;
      inventoryIds.push(item.id);
    });

    setCheckedInventories(newCheckedInventories);

    // Calculate new checked count
    const newCheckedCount = Object.values(newCheckedInventories).filter(Boolean).length;

    if (!disableActions && inventoryIds.length > 0) {
      bulkCheckInventory(
        {
          inventoryIds,
          isChecked: checked,
          serviceTicketId,
        },
        {
          onSuccess: () => {
            message.success(
              `${inventoryIds.length} items ${
                checked ? "checked" : "unchecked"
              } successfully`
            );
            if (onInventoryChecked) {
              onInventoryChecked(inventoryIds, checked, newCheckedCount);
            }
          },
          onError: () => {
            const revertedState = { ...checkedInventories };
            filteredInventories?.forEach((item) => {
              revertedState[item.id] = !checked;
            });
            setCheckedInventories(revertedState);
            message.error("Failed to update inventory check status");
          },
        }
      );
    } else if (onInventoryChecked) {
      onInventoryChecked(inventoryIds, checked, newCheckedCount);
    }
  };

  const showHistory = (inventory: TInventory) => {
    setSelectedInventory(inventory);
    setHistoryModalVisible(true);
  };

  const closeHistory = () => {
    setHistoryModalVisible(false);
    setSelectedInventory(null);
  };

  const getStatusColor = (status: InventoryStatusCode) => {
    switch (status) {
      case InventoryStatusCode.AVAILABLE_LIVE:
        return "green";
      case InventoryStatusCode.AVAILABLE_ON_HAND:
        return "blue";
      case InventoryStatusCode.NO_POSSESSION:
        return "orange";
      case InventoryStatusCode.RELEASED:
        return "purple";
      default:
        return "default";
    }
  };

  const getInventoryCounts = () => {
    const totalCount = filteredInventories?.length || 0;
    const checkedCount = filteredInventories?.filter((item) => checkedInventories[item.id]).length || 0;
    const uncheckedCount = totalCount - checkedCount;
    return { totalCount, checkedCount, uncheckedCount };
  };

  const { totalCount, checkedCount, uncheckedCount } = getInventoryCounts();

  return (
    <div className="inventory-check-list">
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1 mr-4">
            <Search
              placeholder="Search by title, reference, or artist..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </div>
          {showCheckAll && (filteredInventories?.length ?? 0) > 0 && (
            <Button
              type="primary"
              onClick={() => handleCheckAll(!allChecked)}
              className="flex items-center"
              disabled={disableActions}
            >
              <div className="flex items-center">
                {allChecked ? (
                  <>
                    <X size={14} className="mr-1" /> Unconfirm All
                  </>
                ) : (
                  <>
                    <CheckCircle size={14} className="mr-1" /> Confirm All
                  </>
                )}
              </div>
            </Button>
          )}
        </div>

        {totalCount > 0 && (
          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <span className="font-semibold text-green-700 text-base">Confirmed:</span>
                  <span className="ml-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-bold">
                    {checkedCount}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="font-medium text-gray-600 text-sm">Total Inventory on Service:</span>
                  <span className="ml-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                    {totalCount}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="font-medium text-gray-600 text-sm">Not Confirmed:</span>
                  <span className="ml-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">
                    {uncheckedCount}
                  </span>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {totalCount > 0 && `${Math.round((checkedCount / totalCount) * 100)}% complete`}
              </div>
            </div>
          </div>
        )}
      </div>

      <Spin spinning={isLoading}>
        {(filteredInventories?.length ?? 0) === 0 ? (
          <Empty description="No inventory items found" />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={filteredInventories}
            renderItem={(item) => (
              <List.Item
                className="py-3 px-4 border rounded mb-2 hover:bg-gray-50"
                key={item.id}
                actions={[
                  <Button
                    key="history"
                    type="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      showHistory(item);
                    }}
                    icon={<Info size={16} />}
                  />,
                ]}
              >
                <div
                  className="mr-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={checkedInventories[item.id] || false}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCheckInventory(item.id, e.target.checked);
                    }}
                    disabled={disableActions}
                  />
                </div>
                <List.Item.Meta
                  title={
                    <div className="flex items-center flex-wrap">
                      <span className="font-medium mr-2">
                        {item.title || "Untitled"}
                      </span>
                      <Tag color={getStatusColor(item.status)}>
                        {capitalize(item.status)}
                      </Tag>
                    </div>
                  }
                  description={
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mt-1">
                      <div className="flex flex-col">
                        <Text type="secondary" className="text-xs">
                          Art Handler ID:
                        </Text>
                        <Text strong>{item.inventoryRef || "N/A"}</Text>
                      </div>
                      <div className="flex flex-col">
                        <Text type="secondary" className="text-xs">
                          Client Reference:
                        </Text>
                        <Text>{item.clientInternalRef || "N/A"}</Text>
                      </div>
                      <div className="flex flex-col">
                        <Text type="secondary" className="text-xs">
                          Artist:
                        </Text>
                        <Text>{item.artistName || "N/A"}</Text>
                      </div>
                      <div className="flex flex-col">
                        <Text type="secondary" className="text-xs">
                          Type:
                        </Text>
                        <Text>{capitalize(item.itemType)}</Text>
                      </div>
                      <div className="flex flex-col">
                        <Text type="secondary" className="text-xs">
                          Packing:
                        </Text>
                        <Text>{formatPackingType(item.packingType)}</Text>
                      </div>
                      <div className="flex flex-col">
                        <Text type="secondary" className="text-xs">
                          Dimensions:
                        </Text>
                        <Text>{`${item.length}" × ${item.width}" × ${item.height}"`}</Text>
                      </div>
                      <div className="flex flex-col">
                        <Text type="secondary" className="text-xs">
                          Cubic Feet:
                        </Text>
                        <Text>
                          {calculateCubicFeet(
                            item.length,
                            item.width,
                            item.height
                          )}{" "}
                          ft³
                        </Text>
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>

      <Modal
        title={
          <div>
            <div className="font-medium text-base">Inventory History</div>
            {selectedInventory && (
              <div className="text-sm text-gray-500 font-normal mt-1">
                {selectedInventory.title || "Untitled"} • {selectedInventory.inventoryRef || "N/A"}
              </div>
            )}
          </div>
        }
        open={historyModalVisible}
        onCancel={closeHistory}
        footer={null}
        width={900}
      >
        {selectedInventory && (
          <div>
            {isLoadingHistory ? (
              <div className="text-center py-6">Loading history...</div>
            ) : (
              <div className="space-y-2">
                {inventoryHistory?.length === 0 ? (
                  <Empty description="No history found for this inventory" />
                ) : (
                  inventoryHistory?.map((entry: InventoryHistoryEntry) => (
                    <div key={entry.id} className="border rounded p-3 hover:bg-gray-50">
                      <div className="grid grid-cols-6 gap-3 items-center text-sm">
                        {/* Type (Service Type if available) */}
                        <div>
                          <Text type="secondary" className="text-xs block mb-1">Type</Text>
                          <Text className="font-medium">
                            {entry.serviceTicket?.serviceType
                              ? capitalize(entry.serviceTicket.serviceType)
                              : "Status Update"}
                          </Text>
                        </div>

                        {/* Ticket */}
                        <div>
                          <Text type="secondary" className="text-xs block mb-1">Ticket #</Text>
                          <Text strong>
                            {entry.serviceTicket?.serial || "N/A"}
                          </Text>
                        </div>

                        {/* Service Client */}
                        <div>
                          <Text type="secondary" className="text-xs block mb-1">Service Client</Text>
                          <Text className="truncate">
                            {entry.serviceTicket?.client?.companyName ||
                             entry.serviceTicket?.client?.firstName || "N/A"}
                          </Text>
                        </div>

                        {/* Status */}
                        <div>
                          <Text type="secondary" className="text-xs block mb-1">Status</Text>
                          <Tag color={getStatusColor(entry.status)}>
                            {capitalize(entry.status)}
                          </Tag>
                        </div>

                        {/* Date */}
                        <div className="col-span-2">
                          <Text type="secondary" className="text-xs block mb-1">
                            {entry.serviceTicket?.completedAt ? "Completion Date" : "Date"}
                          </Text>
                          <Text>
                            {dayjs(entry.serviceTicket?.completedAt || entry.timestamp).format("MMM D, YYYY")}
                          </Text>
                        </div>
                      </div>

                      {entry.notes && (
                        <div className="mt-2 pt-2 border-t text-sm">
                          <Text type="secondary" className="text-xs">Notes: </Text>
                          <Text>{entry.notes}</Text>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
